import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { usePrivy } from '@privy-io/react-auth';
import { useAuth } from '../contexts/AuthContext';

// Social media icons
const XIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

// Blockchain explorer icons
const SolscanIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.243 18.788L6.55 15.54l4.207-2.436 4.208 2.436-4.208 3.248zm.001-5.248L6.55 10.292l4.208-7.292 4.208 7.292-4.208 3.248z" />
  </svg>
);

const DexscreenerIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-2 16h4v2h-4v-2zm6-3H8v-2h8v2zm0-3H8V8h8v2z" />
  </svg>
);

const DashboardContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  overflow: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div<{ isMobileOpen: boolean }>`
  width: 250px;
  background-color: #0a0a0a;
  border-right: 1px solid ${props => props.theme.colors.primary};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    height: ${props => props.isMobileOpen ? 'auto' : '60px'};
    overflow: ${props => props.isMobileOpen ? 'visible' : 'hidden'};
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.colors.primary};
    transition: height 0.3s ease;
    position: relative;
    padding: ${props => props.isMobileOpen ? '1rem' : '0.5rem 1rem'};
  }
`;

const Logo = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 2rem;
  text-align: center;
  text-shadow: 0 0 10px rgba(254, 189, 35, 0.5);
  
  @media (max-width: 768px) {
    margin-bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  margin: 0;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  
  @media (max-width: 768px) {
    padding-top: 1rem;
  }
`;

interface MenuItemProps {
  disabled?: boolean;
  isActive?: boolean;
}

const MenuItem = styled.li<MenuItemProps>`
  padding: 0.8rem 0.5rem;
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};

  ${props => 
    props.disabled 
      ? css`
          opacity: 0.5;
          cursor: not-allowed;
          &:hover {
            background-color: transparent;
            border-left-color: transparent;
          }
        `
      : css`
          &:hover {
            background-color: rgba(0, 192, 239, 0.1);
            border-left-color: ${props.theme.colors.primary};
          }
          ${props.isActive && css`
            background-color: rgba(0, 192, 239, 0.1);
            border-left-color: ${props.theme.colors.primary};
            color: ${props.theme.colors.primary};
          `}
        `
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    border-left: none;
    border-bottom: 1px solid #222;
    
    ${props => 
      props.disabled
        ? css`
            &:hover, &.active {
              border-left: none;
              border-bottom-color: #222;
            }
          `
        : css`
            &:hover {
              border-left: none;
              border-bottom-color: ${props.theme.colors.primary};
            }
            ${props.isActive && css`
              border-left: none;
              border-bottom-color: ${props.theme.colors.primary};
              color: ${props.theme.colors.primary};
            `}
          `
    }
  }
`;

const LogoutButton = styled.button`
  background-color: transparent;
  border: 1px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.main};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
  }
  
  @media (max-width: 768px) {
    margin-top: 1rem;
    padding: 0.75rem;
  }
`;

const VersionInfo = styled.div`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.text};
  opacity: 0.6;
  margin-top: 1rem;
  text-align: center;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.5rem;
  gap: 10px;
`;

const SocialLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  overflow: auto;
  
  @media (max-width: 768px) {
    height: calc(100vh - 60px);
    padding: 0.75rem;
  }
`;

interface DashboardLayoutProps {
  children: React.ReactNode;
  activeMenuItem?: 'console' | 'wallet' | 'settings' | 'revenue' | 'leaderboard' | 'whitelabel';
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ 
  children, 
  activeMenuItem = 'console' 
}) => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { logout: privyLogout, authenticated: privyAuthenticated } = usePrivy();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    if (privyAuthenticated) {
      await privyLogout();
    }
    logout();
    navigate('/');
  };
  
  const handleMenuItemClick = (route: string, requiresAuth: boolean = false, isExternal: boolean = false) => {
    if (requiresAuth && !isAuthenticated) {
      return;
    }
    
    if (isExternal) {
      window.open(route, '_blank');
    } else {
      navigate(route);
      setIsMobileMenuOpen(false);
    }
  };

  const menuItemsConfig = [
    { name: 'Invest and Earn', route: 'https://earn.voovo.app/', key: 'invest', requiresAuth: false, isExternal: true },
    { name: 'Console', route: '/console', key: 'console', requiresAuth: false },
    { name: 'Wallet', route: '/wallet', key: 'wallet', requiresAuth: true },
    { name: 'Revenue Share', route: '/revenue', key: 'revenue', requiresAuth: true },
    { name: 'WhiteLabel', route: '/whitelabel', key: 'whitelabel', requiresAuth: true },
    { name: 'Settings', route: '/settings', key: 'settings', requiresAuth: true },
  ];

  return (
    <DashboardContainer>
      <Sidebar isMobileOpen={isMobileMenuOpen}>
        <Logo>
          Voovo TRADING
          <MobileMenuToggle onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? '✕' : '☰'}
          </MobileMenuToggle>
        </Logo>
        {(isMobileMenuOpen || window.innerWidth > 768) && (
          <>
            <SidebarMenu>
              {menuItemsConfig.map(item => (
                <MenuItem 
                  key={item.key}
                  isActive={activeMenuItem === item.key && (!item.requiresAuth || isAuthenticated)}
                  disabled={item.requiresAuth && !isAuthenticated}
                  onClick={() => handleMenuItemClick(item.route, item.requiresAuth, item.isExternal)}
                  title={item.requiresAuth && !isAuthenticated ? 'Please log in to access' : item.name}
                >
                  {item.name}
                </MenuItem>
              ))}
            </SidebarMenu>
            {isAuthenticated && (
              <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
            )}
            <VersionInfo>Voovo Trading Bot v1.0.0</VersionInfo>
            <SocialLinks>
              <SocialLink href="https://x.com/Voovo_study" target="_blank" rel="noopener noreferrer">
                <XIcon /> X
              </SocialLink>
              <SocialLink href="https://t.me/voovo_study" target="_blank" rel="noopener noreferrer">
                <TelegramIcon /> Telegram
              </SocialLink>
              <SocialLink href="https://solscan.io/token/H6dZStmJBb5c6YAsnV5JxTbgwJiLjT5zKc4GeBwcpump" target="_blank" rel="noopener noreferrer">
                <SolscanIcon /> Token
              </SocialLink>
              <SocialLink href="https://dexscreener.com/solana/GjkTe6az5xLXpe4NUAtEc1fLxoLkXDH62nUZBxHZtCoC" target="_blank" rel="noopener noreferrer">
                <DexscreenerIcon /> Chart
              </SocialLink>
            </SocialLinks>
          </>
        )}
      </Sidebar>
      <MainContent>
        {children}
      </MainContent>
    </DashboardContainer>
  );
};

export default DashboardLayout; 